<script>

    import { createEventDispatcher } from 'svelte';
    import DataTable from '../DataGrid/DataTable.svelte';
    import Panel from '../Layout/Panel.svelte';
    import Spinner from '../Spinner.svelte';
    import { sgettext } from '../../i18n.js';

    import {
        isEditableTemplate,
    } from './utils.js';
    import ElementNameRenderer from './ColumnRenderers/ElementNameRenderer.svelte';
    import GovernanceRenderer from './ColumnRenderers/GovernanceRenderer.svelte';
    import {
        approvalInfo,
        diff,
        diffLoading,
        elementSource,
        elementTarget,
        selectedElement,
        showFiori,
    } from './diffStore.js';

    export let loading = false;
    export let filterBy = ["ALL"];

    $: modifiedRows = $diff.modified.filter((obj) =>
        filterBy.includes("ALL") || filterBy.length == 0 ? true : filterBy.includes(obj.element.type)
    )
    $: addedRows = $diff.added.filter((obj) =>
        filterBy.includes("ALL") || filterBy.length == 0 ? true : filterBy.includes(obj.element.type)
    )
    $: removedRows = $diff.removed.filter((obj) =>
        filterBy.includes("ALL") || filterBy.length == 0 ? true : filterBy.includes(obj.element.type)
    )

    let columns;

    $: if ($showFiori && $approvalInfo.diffType !== 'RISK') {
        columns = {
            element: {
                label: sgettext('$$NAME$$'),
                keyPath: 'name',
                width: '70%',
                renderer: ElementNameRenderer,
                bgColor: '#fff',
                style: 'cursor: pointer;'
            },
            governance: {
                label: sgettext('$$FIORI_NUMBER$$'),
                renderer: GovernanceRenderer,
                width: '30%',
                bgColor: '#fff',
            }
        }
    } else {
        columns = {
            element: {
                label: sgettext('$$NAME$$'),
                keyPath: 'name',
                width: '100%',
                renderer: ElementNameRenderer,
                bgColor: '#fff',
            },
        }
    }

    function selectElementInDiff(change, isSelected) {
        // Need to create a new array with the additional selected attribute to trigger the refresh
        let changeType;
        if (change.source != null && change.target != null) {
            changeType = 'modified';
        } else if (change.source != null) {
            changeType = 'removed'
        } else {
            changeType = 'added';
        }
        var data = $diff[changeType]
        if (change.target && change.target.element_instance){
            data.forEach(e => {
                if (e.target.element_instance.id == change.target.element_instance.id) {
                    e.selected = isSelected;
                }
            });
        } else{
            data.forEach(e => {
                if (e.element.id == change.element.id) {
                    e.selected = isSelected;
                }
            });
        }
        $diff[changeType] = [...$diff[changeType]];
    }

    function handleRowClick(event, row) {
        if ($selectedElement != null) { selectElementInDiff($selectedElement, false); }
        selectedElement.set(row);
        selectElementInDiff(row, true);

        elementSource.set(row.source);
        elementTarget.set(row.target);
    }

    const dispatch = createEventDispatcher();

    function checkFiori(event) {
        dispatch('checkFiori');
    }
</script>

{#if loading}
  <div class="spinner-div">
    <Spinner size="large" />
  </div>
{/if}

{#if $diff['added'].length == 0 && $diff['modified'].length == 0 && $diff['removed'].length == 0 && !$diffLoading}
  <h5 style="text-align: center; margin-top: 20px">{sgettext('$$ELEMENT_CHANGES_PRESENT$$')}</h5>
{/if}

{#if modifiedRows.length && !$diffLoading}
  <Panel
    title={sgettext('$$ELEMENTS_UPDATED$$')}
    collapsible={false}
    padded={true}
  >
    <div slot="body">
      <DataTable
        rows={modifiedRows}
        {columns}
        small={true}
        border={''}
        dataListRow={true}
        onRowClick={handleRowClick}
        on:handleChange={checkFiori}
      />
    </div>
    <div slot="additionalInfo">
      {#if $showFiori && isEditableTemplate($approvalInfo) && $approvalInfo.diffType != 'RISK'}
        <span class="text-white">* {sgettext('$$ENTER_FIORI_NUMBER$$')}</span>
      {/if}
    </div>
  </Panel>
{/if}

{#if addedRows.length && !$diffLoading}
  <Panel
    title={sgettext('$$ELEMENTS_ADDED$$')}
    collapsible={false}
    padded={true}
  >
    <div slot="body">
      <DataTable
        rows={addedRows}
        {columns}
        small={true}
        border={''}
        dataListRow={true}
        onRowClick={handleRowClick}
        on:handleChange={checkFiori}
      />
    </div>
    <div slot="additionalInfo">
      {#if $showFiori && isEditableTemplate($approvalInfo) && $approvalInfo.diffType != 'RISK'}
        <span class="text-white">* {sgettext('$$ENTER_FIORI_NUMBER$$')}</span>
      {/if}
    </div>
  </Panel>
{/if}

{#if removedRows.length && !$diffLoading}
  <Panel
    title={sgettext('$$ELEMENTS_REMOVED$$')}
    collapsible={false}
    padded={true}
  >
    <div slot="body">
      <DataTable
        rows={removedRows}
        {columns}
        small={true}
        border={''}
        dataListRow={true}
        onRowClick={handleRowClick}
        on:handleChange={checkFiori}
      />
    </div>
    <div slot="additionalInfo">
      {#if $showFiori && isEditableTemplate($approvalInfo) && $approvalInfo.diffType != 'RISK'}
        <span class="text-white">* {sgettext('$$ENTER_FIORI_NUMBER$$')}</span>
      {/if}
    </div>
  </Panel>
{/if}

<style>
  .spinner-div {
    display: flex;
    justify-content: center;
  }
</style>
